export const text = (value) => (value == null ? '' : String(value))
// Study event IDs follow the UUID contract; the legacy EUtils helper returns a short random string.
export function studyUuid(random = globalThis.crypto) {
  if (random && typeof random.randomUUID === 'function') return random.randomUUID()
  if (!random || typeof random.getRandomValues !== 'function')
    throw new Error('浏览器暂不能生成作答标识，请更新浏览器后重试')
  const bytes = random.getRandomValues(new Uint8Array(16))
  bytes[6] = (bytes[6] & 15) | 64
  bytes[8] = (bytes[8] & 63) | 128
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
  return [hex.slice(0, 8), hex.slice(8, 12), hex.slice(12, 16), hex.slice(16, 20), hex.slice(20)].join('-')
}
export const rows = (value) => (Array.isArray(value) ? value : [])
export const count = (value) => (Number.isInteger(Number(value)) && Number(value) >= 0 ? Number(value) : 0)
export const flag = (value) => value === true || value === 1 || value === '1' || value === 'true'
export const percent = (value) =>
  value != null && Number.isFinite(Number(value)) && Number(value) >= 0 && Number(value) <= 1
    ? `${(Number(value) * 100).toFixed(1).replace(/\.0$/, '')}%`
    : '—'
export const scopeKey = (scope) =>
  ['identity_id', 'category_id', 'source', 'chapter_id'].map((key) => text((scope || {})[key])).join('|')

export function nextLearningTask(day, config = {}, preferredScope) {
  const ordered = rows(config.chapters).map((chapter) => scopeKey(chapter.scope))
  return (
    objectiveTasks(day)
      .filter(
        (task) =>
          flag(task.launchable) &&
          count(task.remaining_count) > 0 &&
          !['closed', 'close', 'cancelled', 'transferred'].includes(task.status)
      )
      .slice()
      .sort((a, b) => {
        const rank = (task) =>
          scopeKey(task.scope) === scopeKey(preferredScope)
            ? -1
            : ordered.indexOf(scopeKey(task.scope)) < 0
            ? Number.MAX_SAFE_INTEGER
            : ordered.indexOf(scopeKey(task.scope))
        return rank(a) - rank(b)
      })[0] || null
  )
}
export const statusLabel = (value) =>
  ({
    active: '进行中',
    scheduled: '待开始',
    paused: '已暂停',
    ended: '已结束',
    expired: '已到期',
    in_progress: '进行中',
    completed: '已完成',
    overdue: '待补做',
    rest: '休息日',
    credited: '已计入',
    answer_saved: '已提交',
    ledger_pending: '进度确认中',
    not_started: '待作答',
    rejected: '未计入计划',
    duplicate_not_credited: '已记录',
    closed: '已关闭',
    close: '已关闭',
    open: '待完成',
    pending: '待完成',
    cancelled: '已取消',
    transferred: '已重新安排'
  }[value] ||
  value ||
  '待确认')

export function today(now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Shanghai',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(now)
  const get = (key) => parts.find((part) => part.type === key).value
  return `${get('year')}-${get('month')}-${get('day')}`
}
export function addDays(date, amount) {
  const day = new Date(`${date}T00:00:00Z`)
  day.setUTCDate(day.getUTCDate() + amount)
  return day.toISOString().slice(0, 10)
}
export function periodRange(date, period) {
  if (period === 'day') return [date, date]
  const day = new Date(`${date}T00:00:00Z`)
  if (period === 'week') {
    const start = addDays(date, -((day.getUTCDay() + 6) % 7))
    return [start, addDays(start, 6)]
  }
  return [
    date.slice(0, 7) + '-01',
    new Date(Date.UTC(day.getUTCFullYear(), day.getUTCMonth() + 1, 0)).toISOString().slice(0, 10)
  ]
}
export function shiftPeriod(date, period, offset) {
  if (period !== 'month') return addDays(date, offset * (period === 'week' ? 7 : 1))
  const day = new Date(`${date.slice(0, 7)}-01T00:00:00Z`)
  day.setUTCMonth(day.getUTCMonth() + offset)
  return day.toISOString().slice(0, 10)
}
export function resolveScopes(groups, parent) {
  const seen = new Set(),
    result = []
  for (const group of rows(groups)) {
    if (group.identity_parent_id && text(group.identity_parent_id) !== text(parent)) continue
    const excluded = ['human', 'humam', 'unit', 'autonomous']
    if (excluded.includes(text(group.category).toLowerCase())) continue
    const sons = Array.isArray(group.son) ? group.son : Object.values(group.son || {})
    for (const son of sons) {
      const category = text(son.category).toLowerCase(),
        groupCategory = text(group.category).toLowerCase()
      if (excluded.includes(category) || !son.title) continue
      if (
        son.source
          ? son.source !== 'question'
          : ['exam', 'daily'].includes(category) ||
            ['exam', 'daily'].includes(groupCategory) ||
            !(groupCategory === 'question' || ['question', 'chapter', 'year'].includes(category))
      )
        continue
      const id = son.identity_id > 0 ? son.identity_id : son.stat_identity
      const categoryId = son.category_id || category || group.category_id || groupCategory
      if (!id || !categoryId || excluded.includes(text(categoryId).toLowerCase())) continue
      const scope = {
        identity_parent_id: text(parent),
        identity_id: text(id),
        category_id: text(categoryId),
        source: 'question',
        timezone: 'Asia/Shanghai'
      }
      const key = scopeKey(scope)
      if (seen.has(key)) continue
      seen.add(key)
      result.push({ key, title: son.title, group: group.title, scope })
    }
  }
  return result
}
export function validateReport(report, scope, period, date) {
  if (
    !report ||
    !report.report_id ||
    !report.metrics ||
    typeof report.stats_version === 'boolean' ||
    !text(report.stats_version).trim()
  )
    throw new Error('学习报告返回内容不完整，请重试')
  const actual = report.scope || {},
    window = report.window || {}
  for (const key of [
    'identity_parent_id',
    'identity_id',
    'category_id',
    'source',
    'timezone',
    'chapter_id'
  ]) {
    if (text(actual[key]) !== text(scope[key])) throw new Error('返回的报告与所选题库或章节不一致，请刷新')
  }
  const [start, end] = periodRange(date, period)
  if (
    window.period !== period ||
    window.timezone !== 'Asia/Shanghai' ||
    (window.anchor_date && ![date, start].includes(window.anchor_date)) ||
    (window.start_date && window.start_date !== start) ||
    (window.end_date && window.end_date !== end)
  )
    throw new Error('报告统计周期不一致，请重试')
  const m = report.metrics
  const keys = [
    'attempt_count',
    'unique_questions',
    'correct_count',
    'wrong_count',
    'first_attempt_count',
    'first_correct_count',
    'answer_time_seconds',
    'active_days'
  ]
  if (
    keys.some(
      (key) =>
        m[key] == null ||
        typeof m[key] === 'boolean' ||
        !text(m[key]).trim() ||
        !Number.isInteger(Number(m[key])) ||
        Number(m[key]) < 0
    ) ||
    +m.correct_count + +m.wrong_count !== +m.attempt_count ||
    +m.unique_questions > +m.attempt_count ||
    +m.first_correct_count > +m.first_attempt_count ||
    +m.first_attempt_count > +m.attempt_count
  )
    throw new Error('统计数据不完整，请重试')
  return report
}
export function defaultConfig(parent) {
  return {
    identity_parent_id: text(parent),
    timezone: 'Asia/Shanghai',
    goal: { period: 'day', objective_target_count: 30 },
    start_date: today(),
    end_date: addDays(today(), 29),
    weekdays: [1, 2, 3, 4, 5],
    chapters: [],
    strategy: 'balanced',
    include_wrong_review: true,
    partial_period_policy: 'full_target',
    ordinary_practice_credit: 'explicit_task_only',
    subjective_policy: 'separate_self_check',
    reminder: { enabled: false, local_time: '20:30', channel: 'local' }
  }
}
export function canSelect(chapter) {
  const permission = chapter.permission
  const granted =
    permission && typeof permission === 'object'
      ? flag(permission.granted) && count(permission.lock) === 0
      : permission === 'granted'
  return flag(chapter.selectable) && granted && count(chapter.objective_available_count) > 0
}
export function configError(config, options, effectiveFrom = config.start_date) {
  const goal = config.goal || {},
    limits = options.limits || {}
  const maxTarget = goal.period === 'day' ? limits.max_daily_target : limits.max_period_target
  if (
    !['day', 'week', 'month'].includes(goal.period) ||
    !Number.isInteger(+goal.objective_target_count) ||
    +goal.objective_target_count <= 0 ||
    (maxTarget != null && +goal.objective_target_count > +maxTarget)
  )
    return `请填写有效的目标题量${maxTarget ? `（1～${maxTarget} 题）` : ''}`
  if (
    !effectiveFrom ||
    !config.end_date ||
    effectiveFrom < today() ||
    effectiveFrom < config.start_date ||
    effectiveFrom > config.end_date
  )
    return '请选择今天或之后的有效学习日期'
  const span = (new Date(config.end_date) - new Date(effectiveFrom)) / 86400000
  if (span >= (limits.max_date_span_days || limits.max_span_days || 366)) return '学习区间超过允许范围'
  if (!rows(config.weekdays).length) return '请至少选择一个学习日'
  let learningDay = false
  for (let index = 0; index <= span; index++) {
    const day = new Date(`${addDays(effectiveFrom, index)}T00:00:00Z`)
    if (config.weekdays.includes(((day.getUTCDay() + 6) % 7) + 1)) {
      learningDay = true
      break
    }
  }
  if (!learningDay) return '所选日期内没有学习日，请调整日期或学习日'
  if (!rows(config.chapters).length) return '请至少选择一个可用章节'
  const available = new Map(rows(options.chapter_options).map((row) => [scopeKey(row.scope), row]))
  if (config.chapters.some((row) => !canSelect(available.get(scopeKey(row.scope)) || {})))
    return '部分章节已不可用，请重新选择'
  return ''
}
export const objectiveTasks = (day) =>
  rows((day || {}).tasks).filter(
    (task) =>
      task.kind === 'objective' &&
      flag(task.counts_toward_target) &&
      task.task_role !== 'makeup_proxy' &&
      count(task.effective_target_count) > 0
  )
export const dayComplete = (day) =>
  count((day || {}).effective_target_count) > 0 &&
  count(day.actual_completed_count) >= count(day.effective_target_count) &&
  objectiveTasks(day).length > 0 &&
  objectiveTasks(day).every(
    (task) =>
      count(task.remaining_count) === 0 && count(task.completed_count) >= count(task.effective_target_count)
  )
